import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel, // Added this
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { 
  Check, Minus, Eye, Edit, Trash2, Search, FileText, 
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight 
} from "lucide-react";
import { useSaveProblem, useDeleteProblem } from "../../hooks/useProblems";

const columnHelper = createColumnHelper();

// Change this line near the top:
const ProblemsTable = ({ data, onSelectionChange, onEdit, onViewNote }) => {
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  // Added pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, 
  });

  const { mutate: saveProblem } = useSaveProblem();
  const { mutate: deleteProblem } = useDeleteProblem();

  const toggleStatus = (row) => {
    saveProblem({ ...row.original, status: !row.original.status });
  };

  const columns = [
    columnHelper.display({
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: ({ row, getValue }) => (
        <button 
          onClick={() => toggleStatus(row)}
          className={`flex items-center justify-center w-6 h-6 rounded-md border ${
            getValue() 
              ? "bg-emerald-50 border-emerald-200 text-emerald-600" 
              : "bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-300"
          }`}
        >
          {getValue() ? <Check className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
        </button>
      ),
    }),
    columnHelper.accessor("title", {
      header: "Title",
      cell: (info) => (
        <span className="font-semibold text-slate-800">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("difficulty", {
      header: "Difficulty",
      cell: (info) => {
        const val = info.getValue();
        const styles = {
          "1": "bg-emerald-100 text-emerald-800 border-emerald-200",
          "2": "bg-amber-100 text-amber-800 border-amber-200",
          "3": "bg-red-100 text-red-800 border-red-200"
        };
        const labels = { "1": "Easy", "2": "Medium", "3": "Hard" };
        return (
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${styles[val]}`}>
            {labels[val]}
          </span>
        );
      },
    }),
    columnHelper.accessor("topic", {
      header: "Topics",
      cell: (info) => (
        <div className="flex flex-wrap gap-1 max-w-[200px]">
          {info.getValue()?.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-md uppercase tracking-wider">
              {tag}
            </span>
          ))}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {/* We added the onClick handler here! */}
          {row.original.note && (
            <button 
              onClick={() => onViewNote(row.original)} 
              className="text-blue-500 hover:text-blue-700 transition-colors" 
              title="View Note"
            >
              <FileText className="w-4 h-4" />
            </button>
          )}
          <a href={row.original.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors">
            <Eye className="w-4 h-4" />
          </a>
          <button onClick={() => onEdit(row.original)} className="text-slate-400 hover:text-indigo-600 transition-colors">
            <Edit className="w-4 h-4" />
          </button>
          <button 
            onClick={() => {
              if(window.confirm("Delete this problem?")) deleteProblem([row.original._id]);
            }} 
            className="text-slate-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection, sorting, globalFilter, pagination },
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      setRowSelection(updater);
      setTimeout(() => {
        const selectedRows = table.getSelectedRowModel().rows;
        onSelectionChange(selectedRows.map(r => r.original._id));
      }, 0);
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination, // Handle pagination changes
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(), // Enables pagination math
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
      {/* Search Bar */}
      <div className="p-4 border-b border-slate-200 bg-slate-50/50">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search problems..."
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Table Area */}
      <div className="overflow-x-auto min-h-[400px]">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th 
                    key={header.id} 
                    onClick={header.column.getToggleSortingHandler()}
                    className="px-6 py-4 whitespace-nowrap cursor-pointer select-none hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50 transition-colors group">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-6 py-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  No problems found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-200 bg-slate-50/50 text-sm text-slate-600">
        
        {/* Left: Selected Rows Info & Page Size */}
        <div className="flex items-center gap-4">
          <span className="hidden sm:block">
            {Object.keys(rowSelection).length} of {table.getPreFilteredRowModel().rows.length} selected
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value));
            }}
            className="border border-slate-200 rounded-lg px-2 py-1 bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>

        {/* Right: Navigation Buttons */}
        <div className="flex items-center gap-2">
          <span className="mr-2">
            Page <span className="font-medium text-slate-900">{table.getState().pagination.pageIndex + 1}</span> of{" "}
            <span className="font-medium text-slate-900">{table.getPageCount() || 1}</span>
          </span>

          <div className="flex items-center gap-1">
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className="p-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="p-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="p-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className="p-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProblemsTable;